import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  // Required for HttpOnly refresh cookie to be sent on cross-origin requests.
  withCredentials: true,
});

// Attach access token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ──────────────────────────────────────────────────────────────────────────
// 401 → silent refresh → retry. If refresh fails, kick to /login.
// We queue concurrent 401s while a refresh is in flight, then replay them
// with the new token. This prevents a thundering-herd of refresh calls.
// ──────────────────────────────────────────────────────────────────────────
let refreshPromise = null;
const REFRESH_SKIP_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout', '/auth/request-reset', '/auth/reset-password'];

async function doRefresh() {
  // POST /auth/refresh with credentials; server reads HttpOnly cookie.
  const res = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
    {},
    { withCredentials: true }
  );
  const newToken = res.data?.access_token;
  if (!newToken) throw new Error('No access_token in refresh response');
  localStorage.setItem('token', newToken);
  return newToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';
    const isAuthEndpoint = REFRESH_SKIP_PATHS.some((p) => url.includes(p));

    if (status !== 401 || original._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      // Share an in-flight refresh between concurrent failed calls.
      refreshPromise = refreshPromise || doRefresh().finally(() => { refreshPromise = null; });
      const newToken = await refreshPromise;
      original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
      return api(original);
    } catch (refreshErr) {
      localStorage.removeItem('token');
      const path = window.location.pathname;
      if (!['/login', '/register', '/wachtwoord-vergeten', '/verify-email'].includes(path)) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshErr);
    }
  }
);

/**
 * Veilig een leesbare foutmelding uit een (axios) error halen.
 */
export function extractApiError(err, fallback = 'Er ging iets mis. Probeer het opnieuw.') {
  try {
    const data = err?.response?.data;
    if (!data) return err?.message || fallback;
    if (Array.isArray(data.detail)) {
      const messages = data.detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
      if (messages.length) return messages.join(' · ');
    }
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
    if (typeof data === 'object') return fallback;
    return String(data);
  } catch {
    return fallback;
  }
}

export default api;
